import { z } from 'zod';
import { QueryFunction } from '@tanstack/react-query';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';

/** dd:hh:mm:ss — days may exceed 2 digits when duration is > 99 days. */
const processingTimeSchema = z.string().regex(/^\d{2,}:\d{2}:\d{2}:\d{2}$/);

const LoanProcessingRowSchema = z.object({
  loanNumber: z.string().nullable(),
  creationDate: z.string().nullable(),
  sourceFileName: z.string().nullable(),
  docPullDateTime: z.string().nullable(),
  attachmentFileName: z.string().nullable(),
  docPushDateTime: z.string().nullable(),
  processingTime: processingTimeSchema,
});

const LoanProcessingSummarySchema = z.object({
  loansProcessedCount: z.number().int().nonnegative(),
  documentsProcessedCount: z.number().int().nonnegative(),
  avgProcessingTime: processingTimeSchema,
  medianProcessingTime: processingTimeSchema.optional(),
});

const LoanProcessingMetaSchema = z.object({
  nextCursor: z.string().nullable(),
});

export const GetLoanProcessingResponseSchema = z.object({
  data: z.object({
    summary: LoanProcessingSummarySchema.nullable(),
    rows: z.array(LoanProcessingRowSchema),
    meta: LoanProcessingMetaSchema,
  }),
});

export type LoanProcessingRow = z.infer<typeof LoanProcessingRowSchema>;
export type LoanProcessingSummary = z.infer<typeof LoanProcessingSummarySchema>;
export type LoanProcessingMeta = z.infer<typeof LoanProcessingMetaSchema>;
export type GetLoanProcessingResponse = z.infer<
  typeof GetLoanProcessingResponseSchema
>;

export interface GetLoanProcessingParams {
  dateFrom: string; // YYYY-MM-DD format
  dateTo: string; // YYYY-MM-DD format
  cursor?: string;
  limit?: number;
  /** When true, requests full-range summary/median. Defaults to first page only. */
  includeSummary?: boolean;
}

const buildUrlParams = (params: GetLoanProcessingParams): URLSearchParams => {
  const urlParams = new URLSearchParams();
  if (params.dateFrom) {
    urlParams.append('dateFrom', params.dateFrom);
  }
  if (params.dateTo) {
    urlParams.append('dateTo', params.dateTo);
  }
  if (params.cursor) {
    urlParams.append('cursor', params.cursor);
  }
  urlParams.append('limit', String(params.limit ?? 50));
  if (params.includeSummary !== undefined) {
    urlParams.append('includeSummary', String(params.includeSummary));
  }
  return urlParams;
};

/** Fetches a single page of loan processing metrics (server-side cursor pagination). */
export const fetchLoanProcessing = async (
  params: GetLoanProcessingParams,
): Promise<GetLoanProcessingResponse> => {
  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const urlParams = buildUrlParams(params);
  const url = `/reports/loan-processing?${urlParams.toString()}`;

  const response = await authTokenHttp<unknown>(url, {
    method: 'GET',
  });

  return GetLoanProcessingResponseSchema.parse(response.data);
};

export interface LoanProcessingRangeParams {
  dateFrom: string; // YYYY-MM-DD
  dateTo: string; // YYYY-MM-DD
}

export interface LoanProcessingRangeResult {
  summary: LoanProcessingSummary | null;
  rows: LoanProcessingRow[];
}

/** Page size used when draining a (date-capped) range in one shot. */
const RANGE_FETCH_LIMIT = 100;

/**
 * Drains every page for a bounded date range and returns all rows plus the
 * summary. Intended for use with a capped date range so the total volume stays
 * small enough to render client-side (filters, sorting, total count, CSV).
 */
export const fetchAllLoanProcessing = async (
  params: LoanProcessingRangeParams,
): Promise<LoanProcessingRangeResult> => {
  const rows: LoanProcessingRow[] = [];
  let summary: LoanProcessingSummary | null = null;
  let cursor: string | undefined;
  let isFirstPage = true;

  do {
    const response = await fetchLoanProcessing({
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      cursor,
      limit: RANGE_FETCH_LIMIT,
      // Summary only needs to be computed once (first page).
      includeSummary: isFirstPage,
    });

    rows.push(...response.data.rows);
    if (isFirstPage) {
      summary = response.data.summary;
    }

    cursor = response.data.meta.nextCursor ?? undefined;
    isFirstPage = false;
  } while (cursor);

  return { summary, rows };
};

const getLoanProcessing: QueryFunction<
  GetLoanProcessingResponse,
  ['/analytics/loan-processing', GetLoanProcessingParams]
> = async ({ queryKey }) => {
  const [, params] = queryKey;
  return fetchLoanProcessing(params);
};

export default getLoanProcessing;
